import { queryClient } from '../../../../config/queryClient';
import { queryKeys as financialPlanQueryKeys } from '../../api/queryKeys';
import { queryKeys as goalsQueryKeys } from '../api/queryKeys';
import { ActionStepTypeOptionDTO } from '../../types/actionStepTypeOptionDTO';
import { GoalDTO } from '../../types/goalsDTO';
import { FinancialPlanDTO } from '../../types/financialPlan.types';
import { MonteCarloDTO } from '../types/monteCarlo.types';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { httpBodyOptions } from '../../../CommonUtils/httpBodyOptions';

interface QueryParams {
  clientId: string | number;
  fpId: string;
}

export const invalidateFinancialPlanQuery = async ({ clientId, fpId }: QueryParams) => {
  const queryKey = financialPlanQueryKeys.financialPlan(+clientId, fpId);
  const decumulationQueryKey = financialPlanQueryKeys.decumulationGoal(+clientId, fpId);

  await Promise.all([
    queryClient.invalidateQueries(queryKey),
    queryClient.invalidateQueries(decumulationQueryKey),
  ]);
};

interface FinancialPlan {
  ActionStepTypeOption: ActionStepTypeOptionDTO[];
  Goals: GoalDTO[];
  HasNegativeCashflow: boolean;
}

interface UpdateFinancialPlanQueryData extends QueryParams {
  financialPlan: FinancialPlan;
}

export const updateFinancialPlanQueryData = ({
  clientId,
  fpId,
  financialPlan,
}: UpdateFinancialPlanQueryData) => {
  const queryKey = financialPlanQueryKeys.financialPlan(+clientId, fpId);

  const cacheData = queryClient.getQueryData(queryKey) as FinancialPlanDTO | undefined;
  if (cacheData && cacheData.Goals && financialPlan.Goals) {
    queryClient.setQueryData(queryKey, {
      ...cacheData,
      Goals: financialPlan.Goals,
    });
  }
};

interface RequestBody {
  AllocateExcessCashflow: boolean;
  AllocatedCashflow: number;
  GoalId: string;
  IsInvalidated: boolean;
  WhatIfKey: string;
  WhatIfType: number;
  WhatIfTypeName: string;
}

interface UpdateMonteCarloQueryDataParams extends QueryParams {
  body: RequestBody;
}

export const monteCarloQueryDataRequest = async ({
  clientId,
  fpId,
  body,
}: UpdateMonteCarloQueryDataParams) => {
  const queryKey = goalsQueryKeys.monteCarlo(`${clientId}`, fpId);

  const monteCarloResponse = await authFetch(
    urlWithParams('/Consumer/MC', { clientId, fpId, IsPdf: false }),
    {
      method: 'POST',
      ...httpBodyOptions(body, true),
    },
  );

  const cacheData = queryClient.getQueryData(queryKey) as MonteCarloDTO | undefined;

  if (cacheData && cacheData.Data) {
    queryClient.setQueryData(queryKey, {
      ...cacheData,
      ...monteCarloResponse,
      isChanged: true, // Needed for whatIfsActions.jsx to know that we changed the data
    });
  }
};

interface GetMonteCarloQueryData extends MonteCarloDTO {
  isChanged: boolean;
}

export const getMonteCarloQueryData = ({
  clientId,
  fpId,
}: QueryParams): GetMonteCarloQueryData | Pick<GetMonteCarloQueryData, 'isChanged'> => {
  const queryKey = goalsQueryKeys.monteCarlo(`${clientId}`, fpId);
  const data = queryClient.getQueryData(queryKey) as GetMonteCarloQueryData | undefined;
  return data || { isChanged: false };
};

export const invalidateMonteCarloQuery = async ({ clientId, fpId }: QueryParams) => {
  const queryKey = goalsQueryKeys.monteCarlo(`${clientId}`, fpId);
  await queryClient.invalidateQueries(queryKey);
};
